import { CompanyServicePrice } from '@/types/types';

export type ServicePricesState = {
    pricesByServiceId: Map<number, CompanyServicePrice[]>;
    isLoading: boolean;
    hasError: boolean;
};

const PRICE_PART_SEPARATOR = ' · ';
const OPEN_ENDED_CALENDAR_DATES = new Set(['0001-01-01', '9999-12-31']);
const DAY_NAMES_FROM_MONDAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const CALCULATION_TYPE_UNITS: Record<number, string> = { 2: '/min', 3: '/h', 4: '/day' };

export const buildPricesByServiceId = (prices: CompanyServicePrice[]) => {
    const pricesByServiceId = new Map<number, CompanyServicePrice[]>();

    [...prices].sort(compareByValidFrom).forEach((price) => {
        const serviceId = price.ServiceId ?? 0;
        pricesByServiceId.set(serviceId, [...(pricesByServiceId.get(serviceId) ?? []), price]);
    });

    return pricesByServiceId;
};

const compareByValidFrom = (first: CompanyServicePrice, second: CompanyServicePrice) =>
    (first.From ?? '').localeCompare(second.From ?? '') || (first.Id ?? 0) - (second.Id ?? 0);

export const formatPriceValue = (price: CompanyServicePrice) => {
    const unitSuffix = CALCULATION_TYPE_UNITS[price.CalculationTypeId ?? 0] ?? '';

    return `${formatAmountWithSign(price.Price ?? 0, price.PriceSign)}${unitSuffix}`;
};

const formatAmountWithSign = (amount: number | string, priceSign?: string) =>
    priceSign ? `${amount} ${priceSign}` : `${amount}`;

export const summarizeServicePrices = (prices: CompanyServicePrice[]) =>
    `${formatPriceCount(prices.length)}${PRICE_PART_SEPARATOR}${formatAmountRange(prices)}`;

const formatPriceCount = (priceCount: number) =>
    priceCount === 1 ? '1 price' : `${priceCount} prices`;

const formatAmountRange = (prices: CompanyServicePrice[]) => {
    const amounts = prices.map((price) => price.Price ?? 0);
    const lowestAmount = Math.min(...amounts);
    const highestAmount = Math.max(...amounts);
    const sharedPriceSign = findSharedPriceSign(prices);

    if (lowestAmount === highestAmount) return formatAmountWithSign(lowestAmount, sharedPriceSign);

    return formatAmountWithSign(`${lowestAmount}–${highestAmount}`, sharedPriceSign);
};

const findSharedPriceSign = (prices: CompanyServicePrice[]) => {
    const priceSigns = new Set(prices.map((price) => price.PriceSign ?? ''));

    return priceSigns.size === 1 ? [...priceSigns][0] : undefined;
};

export const getServicePrices = (servicePrices: ServicePricesState, serviceId?: number) =>
    servicePrices.pricesByServiceId.get(serviceId ?? 0) ?? [];

export const formatValidityDates = (from?: string, to?: string) => {
    const startDate = toCalendarDate(from);
    const endDate = toCalendarDate(to);

    if (startDate && endDate) return `${startDate} – ${endDate}`;
    if (startDate) return `from ${startDate}`;
    if (endDate) return `until ${endDate}`;

    return 'any date';
};

const toCalendarDate = (dateTime?: string) => {
    const calendarDate = dateTime?.slice(0, 10);

    if (!calendarDate) return undefined;

    return OPEN_ENDED_CALENDAR_DATES.has(calendarDate) ? undefined : calendarDate;
};

export const formatValidDays = (price: CompanyServicePrice) => {
    if (!price.IsDaysOfWeekSpecific) return 'all days';

    const dayNames = (price.DaysOfWeek ?? []).map(({ DayOfWeekId }) => formatDayName(DayOfWeekId));

    return dayNames.length ? dayNames.join(', ') : 'no days';
};

const formatDayName = (dayOfWeekId?: number) =>
    DAY_NAMES_FROM_MONDAY[(dayOfWeekId ?? 0) - 1] ?? `day #${dayOfWeekId}`;

export const formatValidTimeRange = (price: CompanyServicePrice) => {
    if (!price.IsTimeSpecific) return undefined;

    return `${toHoursAndMinutes(price.FromTime)}–${toHoursAndMinutes(price.ToTime)}`;
};

const toHoursAndMinutes = (time?: string) => time?.slice(0, 5) ?? '?';
